<template>
  <div class="min-h-screen bg-white">

    <!-- Hero Banner -->
    <section class="relative overflow-hidden bg-blog-beige border-b border-blog-gold" style="min-height: 260px;">
      <div class="flex items-center justify-center w-64 h-64 absolute circle-rotating-logo opacity-20 pointer-events-none" style="right: 5%; top: 50%; transform: translateY(-50%);">
        <div class="absolute w-full h-full animate-spin-slow">
          <svg viewBox="0 0 100 100" class="w-full h-full">
            <defs>
              <path id="circlePath-tu" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
            </defs>
            <text class="f-font-times-new-roman-spaced fill-blog-red text-[1rem] uppercase">
              <textPath href="#circlePath-tu">
                Teacher Maestra Victoria  Teacher Maestra Victoria  Teacher Maestra Victoria 
              </textPath>
            </text>
          </svg>
        </div>
      </div>

      <div class="container mx-auto px-8 md:px-20 py-16 relative z-10">
        <p class="text-sm uppercase tracking-[0.25em] font-bold text-blog-old-red/60 mb-3 f-font-helvetica">
          {{ translate('legal') }}
        </p>
        <h1 class="f-font-times-new-roman text-4xl md:text-6xl text-blog-old-red mb-4">
          {{ translate('Terms of Use') }}
        </h1>
        <p class="f-font-helvetica text-blog-old-red/70 text-sm">
          {{ translate('Last updated') }}: {{ lastUpdated }}
        </p>
      </div>
    </section>

    <!-- Content -->
    <main class="px-8 md:px-20 mx-auto py-16 max-w-[900px]">

      <!-- Intro card -->
      <div class="bg-blog-beige border border-blog-gold rounded-[20px] p-8 mb-12">
        <p class="f-font-helvetica text-blog-old-red/80 text-base leading-relaxed">
          {{ translate('Welcome to the') }} <strong class="text-blog-old-red">Teacher Maestra Victoria</strong> {{ translate('platform. By registering, accessing, or using any feature of this platform, you declare that you have read, understood, and agree to these Terms of Use. If you do not agree with any of the conditions, please do not use the platform.') }}
        </p>
      </div>

      <!-- Services overview -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
        <ServiceCard
          v-for="(service, i) in services" :key="i"
          :icon="service.icon"
          :title="translate(service.title)"
          :description="translate(service.description)"
        />
      </div>

      <!-- Sections -->
      <div class="space-y-12">

        <TermSection :number="2" :title="translate('Account registration')">
          <p class="mb-4">{{ translate('To access the services, you must create an account with email and password. You are responsible for:') }}</p>
          <ul class="space-y-3">
            <TermItem icon="pi-lock">{{ translate('Keeping your login credentials confidential.') }}</TermItem>
            <TermItem icon="pi-user">{{ translate('All activities performed on your account.') }}</TermItem>
            <TermItem icon="pi-bell">{{ translate('Immediately notifying any unauthorized use.') }}</TermItem>
          </ul>
          <div class="mt-5 bg-blog-red/5 border border-blog-red/20 rounded-2xl p-4 flex items-start gap-3">
            <i class="pi pi-info-circle text-blog-old-red mt-0.5 shrink-0"></i>
            <p class="text-sm text-blog-old-red/80 f-font-helvetica leading-relaxed">
              {{ translate('Access is personal and non-transferable. The platform uses a') }}
              <strong class="text-blog-old-red">{{ translate('single active session') }}</strong>{{ translate(': a new login on another device will automatically end the previous session.') }}
            </p>
          </div>
        </TermSection>

        <TermSection :number="3" :title="translate('Course purchase')">
          <p class="mb-4">
            {{ translate('Course purchases require advance payment at the prices displayed on the platform at the time of purchase. After payment confirmation, course access is granted immediately.') }}
          </p>
          <p>
            {{ translate('Access to purchased content is') }} <strong>{{ translate('temporary') }}</strong> {{ translate('and set to exactly 1 year (12 months) after purchase.') }}
          </p>
        </TermSection>

        <TermSection :number="4" :title="translate('Refund policy')">
          <div class="bg-blog-beige border-l-4 border-blog-red rounded-r-2xl p-5">
            <p class="text-blog-old-red font-bold f-font-times-new-roman text-lg mb-2">
              {{ translate('Right of withdrawal - 7 days') }}
            </p>
            <p class="text-sm text-blog-old-red/80 f-font-helvetica leading-relaxed">
              {{ translate('Under the') }} <strong>{{ translate('Consumer Protection Code (Art. 49, Law No. 8,078/1990)') }}</strong>,
              {{ translate('you have the right to cancel and receive a full refund within') }}
              <strong>{{ translate('7 (seven) calendar days') }}</strong>
              {{ translate('from the purchase date, provided you have not accessed more than 20% of the course content.') }}
            </p>
          </div>
          <p class="mt-4 text-sm text-blog-old-red/70 f-font-helvetica">
            {{ translate('To request a refund, contact us by email:') }}
            <a :href="`mailto:${context.supportEmail}`"
              class="text-[#ea6b1d] font-bold hover:underline">
              {{ context.supportEmail }}
            </a>
          </p>
        </TermSection>

        <TermSection :number="5" :title="translate('Private lessons')">
          <p class="mb-4">
            {{ translate('The student area allows access to exclusive materials provided by the teacher. Specific conditions (frequency, duration, and pricing) are defined in an individual contract between the student and the teacher.') }}
          </p>
        </TermSection>

        <TermSection :number="6" :title="translate('Intellectual property')">
          <p class="mb-5">
            {{ translate('All platform content - videos, texts, images, teaching materials, and class resources - is the exclusive property of') }} <strong>{{ translate('Escola de Idiomas Teacher Maestra Victoria') }}</strong>.
          </p>
          <p class="text-sm font-bold text-blog-old-red mb-3 uppercase tracking-wide">{{ translate('The following are strictly prohibited:') }}</p>
          <ul class="space-y-3">
            <TermItem icon="pi-times-circle">{{ translate('Reproducing, copying, redistributing, or sharing any content without prior written authorization.') }}</TermItem>
            <TermItem icon="pi-times-circle">{{ translate('Downloading or recording videos.') }}</TermItem>
            <TermItem icon="pi-times-circle">{{ translate('Using materials for commercial purposes or teaching third parties.') }}</TermItem>
            <TermItem icon="pi-times-circle">{{ translate('Removing watermarks, credits, or any authorship identification.') }}</TermItem>
          </ul>
          <p class="mt-4 text-sm text-blog-old-red/60 f-font-helvetica">
            {{ translate('Copyright infringement may result in civil and criminal liability under Law No. 9,610/1998.') }}
          </p>
        </TermSection>

        <TermSection :number="7" :title="translate('User conduct')">
          <p class="mb-4">{{ translate('By using the platform, you agree not to:') }}</p>
          <ul class="space-y-3">
            <TermItem icon="pi-ban">{{ translate('Share offensive, discriminatory, or illegal content.') }}</TermItem>
            <TermItem icon="pi-ban">{{ translate('Attempt to access restricted areas or compromise platform security.') }}</TermItem>
            <TermItem icon="pi-ban">{{ translate('Use automated tools for content extraction.') }}</TermItem>
            <TermItem icon="pi-ban">{{ translate('Engage in any act that may harm other users or platform operations.') }}</TermItem>
          </ul>
        </TermSection>

        <TermSection :number="8" :title="translate('Account suspension and termination')">
          <p class="mb-4">
            {{ translate('We reserve the right to suspend or terminate your account, without prior notice, in case of:') }}
          </p>
          <ul class="space-y-3">
            <TermItem icon="pi-exclamation-triangle">{{ translate('Violation of these Terms of Use.') }}</TermItem>
            <TermItem icon="pi-exclamation-triangle">{{ translate('Improper sharing of protected content.') }}</TermItem>
            <TermItem icon="pi-exclamation-triangle">{{ translate('Fraudulent use of the platform.') }}</TermItem>
          </ul>
          <p class="mt-4 text-sm text-blog-old-red/60 f-font-helvetica">
            {{ translate('In case of termination for cause, no refund will be issued for amounts paid.') }}
          </p>
        </TermSection>

        <TermSection :number="9" :title="translate('Limitation of liability')">
          <p class="mb-4">{{ translate('The platform is provided "as is". We are not responsible for:') }}</p>
          <ul class="space-y-3">
            <TermItem icon="pi-info-circle">{{ translate('Learning outcomes, which depend on each student\'s effort and dedication.') }}</TermItem>
            <TermItem icon="pi-info-circle">{{ translate('Access issues resulting from the user\'s internet connection.') }}</TermItem>
            <TermItem icon="pi-info-circle">{{ translate('Indirect damages or data loss due to third-party failures.') }}</TermItem>
          </ul>
        </TermSection>

        <TermSection :number="10" :title="translate('Privacy')">
          <p>
            {{ translate('The processing of your personal data is governed by our') }}
            <router-link to="/privacy-policy" class="text-[#ea6b1d] font-bold hover:underline">
              {{ translate('Privacy Policy') }}
            </router-link>,
            {{ translate('which is an integral part of these Terms of Use.') }}
          </p>
        </TermSection>

        <TermSection :number="11" :title="translate('Changes to the Terms')">
          <p>
            {{ translate('We may modify these Terms at any time. Significant changes will be communicated by email or platform notice with a minimum of 10 days\' advance notice. Continued use of the platform after changes implies acceptance of the new terms.') }}
          </p>
        </TermSection>

        <TermSection :number="12" :title="translate('Applicable law and jurisdiction')">
          <p>
            {{ translate('These Terms are governed by the laws of the Federative Republic of Brazil. Any disputes will be submitted to the court of the consumer\'s place of residence, pursuant to the Consumer Protection Code.') }}
          </p>
        </TermSection>

        <!-- Contact -->
        <div class="bg-blog-red rounded-[28px] p-8 text-white text-center border-2 border-blog-gold">
          <h2 class="f-font-times-new-roman italic text-2xl md:text-3xl mb-4">
            {{ translate('Contact us') }}
          </h2>
          <p class="f-font-helvetica font-light mb-2">{{ context.companyName }}</p>
          <p class="f-font-helvetica font-light text-white/70 mb-4">CNPJ: {{ context.companyCNPJ }}</p>
          <a
            :href="`mailto:${context.supportEmail}`"
            class="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors border border-white/30 text-white px-6 py-3 rounded-full text-sm font-bold tracking-wide"
          >
            <i class="pi pi-envelope"></i>
            {{ context.supportEmail }}
          </a>
        </div>

      </div>
    </main>
  </div>
</template>

<script setup>
import { translate } from '@/composables/translate';
import { useContextStore } from '@/stores/context';

const context = useContextStore();
const lastUpdated = translate('july 2026');

const services = [
  {
    icon: 'pi-play-circle',
    title: 'Online courses',
    description: 'Purchase and access recorded English courses, available at any time.'
  },
  {
    icon: 'pi-desktop',
    title: 'Streaming',
    description: 'Watch your purchased content directly on the platform, at your own pace.'
  },
  {
    icon: 'pi-calendar',
    title: 'Private lessons',
    description: 'Exclusive materials and personalized pedagogical support.'
  }
];
</script>

<!-- Sub-componentes inline -->
<script>
import { defineComponent, h } from 'vue';

export const TermSection = defineComponent({
  props: { number: Number, title: String },
  setup(props, { slots }) {
    return () => h('section', { class: 'border-t border-blog-gold pt-10' }, [
      h('div', { class: 'flex items-baseline gap-4 mb-6' }, [
        h('span', {
          class: 'f-font-times-new-roman text-5xl text-blog-old-red/20 leading-none select-none'
        }, String(props.number).padStart(2, '0')),
        h('h2', {
          class: 'f-font-times-new-roman text-2xl md:text-3xl text-blog-old-red'
        }, props.title),
      ]),
      h('div', { class: 'f-font-helvetica text-blog-old-red/80 leading-relaxed text-base' }, slots.default?.()),
    ]);
  }
});

export const TermItem = defineComponent({
  props: { icon: { type: String, default: 'pi-circle' } },
  setup(props, { slots }) {
    return () => h('li', { class: 'flex items-start gap-3' }, [
      h('i', { class: `pi ${props.icon} text-blog-old-red mt-1 shrink-0 text-sm` }),
      h('span', { class: 'text-blog-old-red/80 text-sm leading-relaxed' }, slots.default?.()),
    ]);
  }
});

export const ServiceCard = defineComponent({
  props: { icon: String, title: String, description: String },
  setup(props) {
    return () => h('div', {
      class: 'bg-[#fff8f1] text-blog-old-red p-6 rounded-[28px] border border-blog-gold shadow-sm flex flex-col gap-3 transition-transform duration-300 hover:scale-105'
    }, [
      h('i', { class: `pi ${props.icon} text-2xl text-blog-old-red/40` }),
      h('h3', { class: 'f-font-times-new-roman font-bold text-lg text-blog-old-red italic' }, props.title),
      h('p', { class: 'f-font-helvetica text-sm text-gray-700 leading-relaxed font-light' }, props.description),
    ]);
  }
});
</script>